/**
 * Centralized copy for background-upload user-facing strings. Kept together
 * so future i18n can swap them in one place instead of hunting through
 * retry / orchestrator paths.
 */

const RESUBMIT_SUFFIX = 'please re-submit the request.';

const uploadRetryMessages = {
  retryUnavailable: `Retry unavailable — ${RESUBMIT_SUFFIX}`,
  fileNotInCache: `File no longer available on this device — ${RESUBMIT_SUFFIX}`,
  retryFailedForFile: (fileName: string) =>
    `Couldn't retry "${fileName}" — ${RESUBMIT_SUFFIX}`,
  retryToastFailure: (fileName: string, reason: string) =>
    `Retry failed for "${fileName}": ${reason}`,
  retryFailedToStart: 'Retry failed to start',
  retryProviderUnavailable: `Open the request and try again — ${RESUBMIT_SUFFIX}`,
  retryLimitReached: `Retry limit reached — ${RESUBMIT_SUFFIX}`,
  // Not a failure: another trigger already owns this record and will drive it to success or to a
  // real failure. Worded so the user waits rather than clicking again.
  retryAlreadyInFlight: 'This upload is already being retried — give it a moment.',
};

const dismissMessages = {
  removed:
    "Upload cleared from this device. The request will keep showing as 'uploading' on the server until you cancel or resubmit it from the request page.",
};

// Curated, user-facing error copy for the failed-upload tooltip. Anything not
// in this map is treated as internal detail and we hide the tooltip rather
// than expose a raw tus / API string to the user.
const ATTACH_ERROR_COPY = "Couldn't attach the upload. Click Retry to try again.";
const RESUME_ERROR_COPY = "Couldn't resume this upload. Click Retry to try again.";
const NO_MEDIA_ID_COPY = "Upload didn't return a media id. Click Retry to try again.";

const knownErrorCopy: Record<string, string> = {
  'Failed to attach media': ATTACH_ERROR_COPY,
  'No media ID received': NO_MEDIA_ID_COPY,
  'Resume failed': RESUME_ERROR_COPY,
  [uploadRetryMessages.retryUnavailable]: uploadRetryMessages.retryUnavailable,
  [uploadRetryMessages.fileNotInCache]: uploadRetryMessages.fileNotInCache,
  [uploadRetryMessages.retryFailedToStart]: uploadRetryMessages.retryFailedToStart,
  [uploadRetryMessages.retryProviderUnavailable]:
    uploadRetryMessages.retryProviderUnavailable,
  [uploadRetryMessages.retryLimitReached]: uploadRetryMessages.retryLimitReached,
};

/**
 * Map a raw error string from the upload pipeline to user-facing copy.
 * Returns `null` when the error is internal/uncurated — the caller should
 * hide the tooltip rather than expose tus/API jargon.
 */
const formatUserFacingError = (raw: string | undefined): string | null => {
  if (!raw) return null;
  return knownErrorCopy[raw] ?? null;
};

export {
  uploadRetryMessages,
  dismissMessages,
  formatUserFacingError,
};
